// ─────────────────────────────────────────────────────────────────
// Design tokens — the naming rule shared by the build and the editor
//
// The token cascade itself (theme manifest defaults ← site ← document)
// is resolved by the build engine, which is the only place that knows
// how a given medium layers it. What both sides must agree on is the
// *name* a token gets once it becomes CSS, because the two places that
// consume a document's design are:
//
//   · the built page — `tokensToCss()` emits them into `:root`
//   · the editor canvas — EditorCanvas emits the same names on the paper,
//     so a block previews the document's own type and colour instead of
//     the editor's.
//
// One function, so an editor canvas can never disagree with the page it
// is previewing about which variable holds `colorPrimary`.
// ─────────────────────────────────────────────────────────────────

/** `colorPrimary` → `--color-primary`, the name used by the built page's `:root`. */
export function designTokenVar(key: string): string {
  return '--' + key.replace(/([A-Z])/g, '-$1').toLowerCase();
}

/**
 * Everything the editor needs to preview a document's design: the resolved
 * token cascade the *build* will use, plus the content width it renders at.
 *
 * Served by `GET /api/posts/:id/design` and `GET /api/emails/:id/design`, which
 * resolve both through the build engine — so the canvas cannot show a palette
 * or a column width that the published page/email will not have.
 */
export interface DocumentDesign {
  /** The cascade, keyed by token name (`colorPrimary`, `fontBody`, …). */
  tokens: Record<string, string>;
  /** Content column width in px. Emails are fixed-width; posts use `maxWidth`. */
  width: number;
}
